import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, BookOpenCheck, CheckCircle2, Download, FileBarChart2, RefreshCcw, Save, ScrollText, ShieldAlert, Stamp } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const statusMeta = {
  complete:        { label: "Completa",         color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  incomplete:      { label: "Incompleta",       color: "bg-amber-50 text-amber-800 border-amber-200" },
  not_applicable:  { label: "Non applicabile",  color: "bg-slate-100 text-slate-600 border-slate-200" },
};

function StatusBadge({ status, testId }) {
  const meta = statusMeta[status] || statusMeta.incomplete;
  return <Badge className={`border ${meta.color}`} data-testid={testId}>{meta.label}</Badge>;
}

function renderBlock(block, idx) {
  if (block.type === "paragraph") {
    return <p key={idx} className="text-sm leading-relaxed text-slate-700" data-testid={`block-paragraph-${idx}`}>{block.value}</p>;
  }
  if (block.type === "kv") {
    return (
      <div key={idx} className="flex flex-col gap-1 rounded-md border border-slate-100 bg-slate-50 p-3 text-sm sm:flex-row sm:justify-between" data-testid={`block-kv-${idx}`}>
        <span className="font-semibold text-slate-800">{block.label}</span>
        <span className="text-slate-600">{block.value}</span>
      </div>
    );
  }
  if (block.type === "matrix") {
    const rows = block.rows || [];
    return (
      <div key={idx} className="overflow-x-auto" data-testid={`block-matrix-${idx}`}>
        <table className="w-full border-collapse text-center text-sm">
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const isHeader = ri === 0 || ci === 0;
                  const className = isHeader
                    ? "border border-slate-200 bg-slate-100 px-3 py-2 font-semibold text-slate-800"
                    : "border border-slate-200 px-3 py-2 text-slate-700";
                  return <td key={ci} className={className}>{cell}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

export default function DvrPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true); setError("");
    try { setPreview(await apiRequest(`/companies/${id}/dvr-preview`)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (!id) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="preview-no-company">
        <p className="text-slate-600">Apri la scheda di un&apos;azienda e clicca &quot;Anteprima DVR&quot; per generare la bozza tecnica.</p>
      </div>
    );
  }
  if (!preview) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="preview-loading">Caricamento anteprima DVR (20 sezioni)...</div>;

  const exportHtml = async () => {
    setError("");
    try { await downloadFile(`/companies/${id}/dvr-preview/html`, `Bozza_DVR_${preview.company.business_name.replace(/\s+/g, "_")}.html`); }
    catch (err) { setError(err.message); }
  };

  const saveVersion = async () => {
    setError(""); setInfo(""); setSaving(true);
    try {
      const result = await apiRequest(`/companies/${id}/dvr-preview/save-version`, { method: "POST" });
      const versionMsg = result.new_version_number ? `Creata nuova versione v${result.new_version_number}.` : "";
      setInfo(`${result.detail} ${versionMsg} Snapshot archiviato nei Documenti (categoria DVR).`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sections = preview.sections || [];
  const completeCount = sections.filter((s) => s.status === "complete").length;
  const naCount = sections.filter((s) => s.status === "not_applicable").length;
  const incompleteCount = sections.filter((s) => s.status === "incomplete").length;

  return (
    <div className="space-y-6" data-testid="dvr-preview-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="preview-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="preview-refresh-button"><RefreshCcw className="h-4 w-4" /> Genera anteprima</Button>
          <Button variant="outline" onClick={saveVersion} disabled={saving} data-testid="preview-save-button"><Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva versione"}</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={exportHtml} data-testid="preview-export-button"><Download className="h-4 w-4" /> Esporta bozza HTML</Button>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-8" data-testid="preview-cover">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-900">Bozza tecnica DVR · D.Lgs. 81/2008</p>
        <h2 className="mt-3 font-heading text-5xl font-bold text-slate-950" data-testid="preview-company-name">{preview.company.business_name}</h2>
        <p className="mt-2 text-slate-600" data-testid="preview-meta">ATECO {preview.company.ateco_code || "—"} · P.IVA {preview.company.vat_number || "—"} · Generato il {new Date(preview.generated_at).toLocaleString("it-IT")}</p>
        <p className="mt-1 text-sm text-slate-500" data-testid="preview-legal-reference">{preview.legal_reference_version}</p>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          <div className="rounded-md bg-white p-3 shadow-sm"><p className="text-xs uppercase tracking-wider text-slate-500">Completamento generale</p><p className="font-heading text-2xl font-bold text-slate-950" data-testid="preview-overall-completion">{preview.overall_completion}%</p><Progress value={preview.overall_completion} className="mt-2 h-2" /></div>
          <div className="rounded-md bg-emerald-50 p-3 shadow-sm"><p className="text-xs uppercase tracking-wider text-emerald-700">Sezioni complete</p><p className="font-heading text-2xl font-bold text-emerald-900" data-testid="preview-complete-count">{completeCount} / {sections.length}</p></div>
          <div className="rounded-md bg-amber-50 p-3 shadow-sm"><p className="text-xs uppercase tracking-wider text-amber-700">Da completare</p><p className="font-heading text-2xl font-bold text-amber-900" data-testid="preview-incomplete-count">{incompleteCount}</p></div>
          <div className="rounded-md bg-slate-100 p-3 shadow-sm"><p className="text-xs uppercase tracking-wider text-slate-600">Non applicabili</p><p className="font-heading text-2xl font-bold text-slate-700" data-testid="preview-na-count">{naCount}</p></div>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="preview-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {info && <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="preview-info"><CheckCircle2 className="h-4 w-4" /><AlertTitle>Salvataggio completato</AlertTitle><AlertDescription>{info}</AlertDescription></Alert>}

      {preview.can_save_new_version && (
        <Alert className="border-blue-200 bg-blue-50 text-blue-900" data-testid="preview-new-version-hint">
          <Stamp className="h-4 w-4" />
          <AlertTitle>Assessment firmato</AlertTitle>
          <AlertDescription>Il DVR corrente risulta firmato (signed). Cliccando &quot;Salva versione&quot; verrà creata automaticamente una nuova versione di lavoro (draft) con i dati attuali.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4" data-testid="preview-sections">
        {sections.map((section) => (
          <Card key={section.key} className="shadow-none" data-testid={`preview-section-${section.key}`}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-heading flex items-center gap-2 text-xl">
                  <span className="rounded-md bg-blue-900 px-2 py-1 text-sm text-white" data-testid={`preview-section-number-${section.key}`}>{section.number}</span>
                  <FileBarChart2 className="h-5 w-5 text-blue-900" />
                  {section.title}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <StatusBadge status={section.status} testId={`section-status-${section.key}`} />
                  {section.status !== "not_applicable" && <span className="text-sm text-slate-500" data-testid={`section-completion-${section.key}`}>{section.completion}%</span>}
                </div>
              </div>
              {section.summary && <p className="mt-2 text-sm text-slate-600" data-testid={`section-summary-${section.key}`}>{section.summary}</p>}
            </CardHeader>
            <CardContent className="space-y-3">
              {section.status === "not_applicable" && (
                <Alert className="border-slate-200 bg-slate-50 text-slate-700" data-testid={`section-na-${section.key}`}>
                  <BookOpenCheck className="h-4 w-4" />
                  <AlertTitle>Non applicabile sulla base dei dati inseriti</AlertTitle>
                  <AlertDescription>{section.not_applicable_reason || "Motivazione non specificata."}</AlertDescription>
                </Alert>
              )}

              {section.warnings && section.warnings.length > 0 && (
                <Alert className="border-amber-200 bg-amber-50 text-amber-900" data-testid={`section-warning-${section.key}`}>
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Avvisi da risolvere</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc space-y-1">
                      {section.warnings.map((w, idx) => <li key={idx} data-testid={`section-warning-item-${section.key}-${idx}`}>{w}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {section.body_blocks && section.body_blocks.length > 0 && (
                <div className="space-y-2" data-testid={`section-blocks-${section.key}`}>
                  {section.body_blocks.map((block, idx) => renderBlock(block, idx))}
                </div>
              )}

              {section.items && section.items.length > 0 && (
                <div className="space-y-2" data-testid={`section-items-${section.key}`}>
                  {section.items.slice(0, 25).map((item, idx) => (
                    <div key={idx} className="flex flex-col gap-1 rounded-md border border-slate-100 p-3 text-sm sm:flex-row sm:justify-between" data-testid={`section-item-${section.key}-${idx}`}>
                      <span className="font-semibold text-slate-800">{item.label}</span>
                      <span className="text-slate-600">{item.value}</span>
                    </div>
                  ))}
                  {section.items.length > 25 && <p className="text-xs text-slate-500" data-testid={`section-overflow-${section.key}`}>… e altri {section.items.length - 25} elementi nella bozza esportata.</p>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Alert className="border-blue-200 bg-blue-50" data-testid="preview-disclaimer">
        <ScrollText className="h-4 w-4 text-blue-900" />
        <AlertTitle className="text-blue-950">Disclaimer tecnico</AlertTitle>
        <AlertDescription className="text-blue-900">{preview.disclaimer}</AlertDescription>
      </Alert>
    </div>
  );
}
